import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from './ui/alert-dialog';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { usePrompt } from '../hooks/useConfirm';
import {
  Search, Plus, X, Bookmark, Edit2, Star, Loader2, BookOpen, Zap,
  Clock, UtensilsCrossed, Camera, Scan, Check, Pencil, ArrowLeft,
  MoreVertical, Copy, ArrowRightLeft, Save
} from 'lucide-react';
import { Tooltip as UITooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import client from '../api/client';
import { dietApi } from '../api/diet';
import { useAutosaveEditor } from '../hooks/useAutosaveEditor';
import EditorAutosaveBar from './ui/EditorAutosaveBar';

const MEAL_TYPES = [
  { value: 'breakfast', label: 'breakfast' },
  { value: 'lunch', label: 'lunch' },
  { value: 'dinner', label: 'dinner' },
  { value: 'snack', label: 'snack' },
];

function SuggestionFoodRow({ food, onEditServing, onRemove, isEditing, onSaveServing, onCancelServing, onCopyMove }) {
  const { t } = useTranslation();
  const s = food.servings || 1;
  const [draftServings, setDraftServings] = React.useState(s);
  const [draftServingId, setDraftServingId] = React.useState(food.selected_serving_id || '');
  const servingOptions = Array.isArray(food.food_servings) ? food.food_servings : [];
  const hasServingChoices = servingOptions.length > 1;

  React.useEffect(() => {
    if (isEditing) {
      setDraftServings(food.servings || 1);
      setDraftServingId(food.selected_serving_id || '');
    }
  }, [isEditing, food.servings, food.selected_serving_id]);

  return (
    <div className="px-3 py-1 text-sm border-b last:border-0">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium truncate flex-1">{food.food_name}</p>
        {!isEditing && (
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7 text-muted-foreground hover:text-primary" tooltip={t('tooltips.edit')} onClick={onEditServing}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            {onCopyMove && (
              <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7 text-muted-foreground hover:text-primary" tooltip={t('dietSuggestion.copyOrMove', 'Copy / move')} onClick={onCopyMove} data-testid="food-copy-move-btn">
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7 text-muted-foreground hover:text-destructive" tooltip={t('tooltips.remove')} onClick={onRemove}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
      {isEditing ? (
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          {hasServingChoices ? (
            <Select value={draftServingId} onValueChange={setDraftServingId}>
              <SelectTrigger className="h-11 md:h-9 md:h-7 text-xs flex-1 min-w-[140px]">
                <SelectValue placeholder={t('dietSuggestion.selectServing', 'Select serving...')} />
              </SelectTrigger>
              <SelectContent>
                {servingOptions.map(so => (
                  <SelectItem key={so.serving_id} value={so.serving_id} className="text-xs">
                    {so.description} ({Math.round(so.calories || 0)} kcal)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-xs text-muted-foreground shrink-0">{(food.serving_description && !/^[0-9.]* ?servings?$/i.test(food.serving_description)) ? food.serving_description : food.serving_size != null ? `${food.serving_size}${food.serving_unit ?? 'g'}` : 'serving'}</span>
          )}
          <input
            type="number"
            min="0.25"
            step="0.25"
            value={draftServings}
            onChange={e => setDraftServings(parseFloat(e.target.value) || 1)}
            className="w-16 h-11 md:h-7 px-2 text-sm md:text-xs rounded border border-border bg-background text-foreground focus:outline-none focus:border-primary"
            autoFocus
          />
          <Button size="icon" className="h-11 w-11 md:h-7 md:w-7 bg-primary hover:bg-primary/90 text-white shrink-0" tooltip={t('tooltips.confirmServings')} onClick={() => onSaveServing({ servings: draftServings, servingId: draftServingId })}>
            <Check className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7 shrink-0" tooltip={t('tooltips.cancelEdit')} onClick={onCancelServing}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground mt-0.5">
          {s} × {(food.serving_description && !/^[0-9.]* ?servings?$/i.test(food.serving_description)) ? food.serving_description : food.serving_size != null ? `${food.serving_size}${food.serving_unit ?? 'g'}` : 'serving'} · {Math.round((food.calories || 0) * s)} kcal · P {((food.protein || 0) * s).toFixed(1)}g · C {((food.carbs || 0) * s).toFixed(1)}g · F {((food.fat || 0) * s).toFixed(1)}g
        </p>
      )}
    </div>
  );
}

// Build the editor document ({ suggestionForm, daysOfWeek, suggestionName }) for a given
// editingSuggestion (or a blank new-suggestion default). Shared by the hook's initialDocument
// and the on-open reset effect so the mapping logic stays in one place.
function buildSuggestionDoc(editingSuggestion, clientId) {
  if (editingSuggestion) {
    return {
      suggestionForm: {
        client_id: clientId, notes: editingSuggestion.notes || '',
        meals: (() => {
          const result = [];
          let nextId = 100;
          const knownTypes = MEAL_TYPES.map(mt => mt.value);
          MEAL_TYPES.forEach(mt => {
            const matching = (editingSuggestion.meals || []).filter(m =>
              m.meal_type?.toLowerCase() === mt.value ||
              (m.meal_name && m.meal_name.toLowerCase().trim() === mt.value)
            );
            if (matching.length > 0) {
              matching.forEach(m => result.push({ ...m, meal_type: mt.value, option_name: m.option_name || null, _tabId: nextId++ }));
            } else {
              result.push({ meal_type: mt.value, option_name: null, foods: [], _tabId: nextId++ });
            }
          });
          // Non-standard meal types (custom names added by coach)
          (editingSuggestion.meals || []).forEach(m => {
            if (!knownTypes.includes(m.meal_type?.toLowerCase())) {
              result.push({ ...m, option_name: m.option_name || null, _tabId: nextId++ });
            }
          });
          return result;
        })()
      },
      daysOfWeek: editingSuggestion.days_of_week || [],
      suggestionName: editingSuggestion.name || '',
    };
  }
  return {
    suggestionForm: {
      client_id: clientId, notes: '',
      meals: [
        { meal_type: 'breakfast', option_name: null, foods: [], _tabId: 1 },
        { meal_type: 'lunch',     option_name: null, foods: [], _tabId: 2 },
        { meal_type: 'dinner',    option_name: null, foods: [], _tabId: 3 },
        { meal_type: 'snack',     option_name: null, foods: [], _tabId: 4 },
      ]
    },
    daysOfWeek: [],
    suggestionName: '',
  };
}

export default function CreateDietSuggestionModal({ open, onOpenChange, clientId, clientName, onSuccess, editingSuggestion, existingSuggestions = [] }) {
  const { t, i18n } = useTranslation();
  const prompt = usePrompt();

  // --- Editor document (autosave + undo/redo) ---
  // The DOCUMENT is { suggestionForm, daysOfWeek, suggestionName }. Everything else below is
  // transient UI state. A brand-NEW suggestion must NEVER autosave (creating it notifies the
  // client) — the save() below early-returns unless we're editing an existing suggestion (idRef).
  const idRef = useRef(editingSuggestion?.id ?? null);
  const {
    document: editorDoc, update, undo, redo, canUndo, canRedo,
    status: saveStatus, flush: flushSave,
  } = useAutosaveEditor({
    initialDocument: buildSuggestionDoc(editingSuggestion, clientId),
    save: async (d) => {
      if (!idRef.current) return; // NEW suggestion: never autosaves (explicit Send only)
      const nonEmptyMeals = d.suggestionForm.meals.filter(m => (m.foods?.length ?? 0) > 0);
      const payload = { ...d.suggestionForm, name: d.suggestionName, meals: nonEmptyMeals, days_of_week: d.daysOfWeek };
      await dietApi.updateSuggestion(idRef.current, payload);
    },
  });
  const suggestionForm = editorDoc.suggestionForm;
  const daysOfWeek = editorDoc.daysOfWeek;
  const suggestionName = editorDoc.suggestionName;
  // Always-current snapshot of the document for use inside async close handlers.
  const editorDocRef = useRef(editorDoc);
  editorDocRef.current = editorDoc;

  const [suggestionMealIdx, setSuggestionMealIdx] = useState(0);
  const [quickMeals, setQuickMeals] = useState([]);
  const [replacingFoodIdx, setReplacingFoodIdx] = useState(null);
  const [editingServingIdx, setEditingServingIdx] = useState(null);
  // Target picker: { kind: 'food'|'option', mode: 'copy'|'move'|'duplicate', sourceMealIdx, sourceFoodIdx? }
  const [targetPicker, setTargetPicker] = useState(null);
  const [targetsExpanded, setTargetsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('search');
  const [clientTargets, setClientTargets] = useState(null);

  // FatSecret search state
  const [searchQuery, setSearchQuery] = useState('');
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [fatsecretResults, setFatsecretResults] = useState([]);
  const [fatsecretLoading, setFatsecretLoading] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [selectedServing, setSelectedServing] = useState(null);
  const [fatsecretDetail, setFatsecretDetail] = useState(null);
  const [quantity, setQuantity] = useState(1);

  // History & My Foods
  const [recentFoods, setRecentFoods] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [myFoods, setMyFoods] = useState([]);

  // Recipe state
  const [recipeQuery, setRecipeQuery] = useState('');
  const [recipeResults, setRecipeResults] = useState([]);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeDetail, setRecipeDetail] = useState(null);

  // Photo scan state
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoResult, setPhotoResult] = useState(null);
  const [photoFoods, setPhotoFoods] = useState([]);

  // Unsaved changes protection — track any user interaction that modifies the form
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [formTouched, setFormTouched] = useState(false);
  const hasUnsavedChanges = formTouched;

  const handleCloseAttempt = async () => {
    if (editingSuggestion) {
      // EDIT existing: autosave persists everything. Do a final authoritative save on close so we
      // get a real failure signal (the hook's flush swallows errors and retries in the background).
      // On failure, keep the modal open so the coach's changes aren't lost.
      try {
        await flushSave(); // resolve any in-flight/pending debounced save first
        const d = editorDocRef.current;
        const nonEmptyMeals = d.suggestionForm.meals.filter(m => (m.foods?.length ?? 0) > 0);
        await dietApi.updateSuggestion(editingSuggestion.id, {
          ...d.suggestionForm, name: d.suggestionName, meals: nonEmptyMeals, days_of_week: d.daysOfWeek,
        });
        if (onSuccess) onSuccess();
        onOpenChange(false);
      } catch {
        toast.error(t('autosave.saveFailedStayOpen', 'Could not save — still trying. Your changes are kept.'));
      }
      return;
    }
    // NEW compose: nothing autosaves, so guard unsaved work with the discard confirm.
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
    } else {
      onOpenChange(false);
    }
  };

  // Wrap form setters to track touches (skipped during initial load via formTouched reset).
  // All DOCUMENT mutations funnel through the autosave hook's `update`. formTouched is still
  // needed for the NEW-suggestion unsaved-changes confirm-on-close guard.
  const touchAndSetForm = (updater) => {
    setFormTouched(true);
    update(d => { d.suggestionForm = typeof updater === 'function' ? updater(d.suggestionForm) : updater; });
  };
  const touchAndSetName = (v) => {
    setFormTouched(true);
    update(d => { d.suggestionName = v; });
  };
  const touchAndSetDays = (updater) => {
    setFormTouched(true);
    update(d => { d.daysOfWeek = typeof updater === 'function' ? updater(d.daysOfWeek) : updater; });
  };

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      // Sync the autosave id (only an existing suggestion may autosave) and reset the document.
      idRef.current = editingSuggestion?.id ?? null;
      update(buildSuggestionDoc(editingSuggestion, clientId));
      setSuggestionMealIdx(0);
      setFormTouched(false);
      setShowDiscardConfirm(false);
      clearSelection();
      setSearchQuery('');
      setFatsecretResults([]);
      setAutocompleteResults([]);
      setActiveTab('search');
      setRecipeQuery('');
      setRecipeResults([]);
      setSelectedRecipe(null);
      setRecipeDetail(null);
      setPhotoResult(null);
      setPhotoFoods([]);
      setReplacingFoodIdx(null);
      client.get('/meal-templates').then(r => setQuickMeals(r.data)).catch(() => {});
      // Load history & my foods
      setRecentLoading(true);
      client.get('/foods/recent', { params: { limit: 50 } }).then(r => { setRecentFoods(r.data.foods || []); setRecentLoading(false); }).catch(() => setRecentLoading(false));
      client.get('/foods').then(r => setMyFoods(r.data || [])).catch(() => {});
      setClientTargets(null);
      if (clientId) {
        client.get(`/users/${clientId}`).then(r => setClientTargets(r.data || null)).catch(() => {});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId, editingSuggestion?.id, update]);

  // Autocomplete disabled — search uses local community DB only (matches DietPage)
  useEffect(() => {
    setAutocompleteResults([]);
    setShowAutocomplete(false);
  }, [searchQuery, i18n?.language]);

  // Full search: community foods (local DB) — matches DietPage exactly
  const searchRef = useRef(0);
  useEffect(() => {
    if (activeTab !== 'search' || !searchQuery || searchQuery.length < 2) {
      setFatsecretResults([]); setFatsecretLoading(false); return;
    }
    const reqId = ++searchRef.current;
    setFatsecretLoading(true);
    const timer = setTimeout(async () => {
      try {
        const language = i18n?.language?.startsWith('pt') ? 'pt' : 'en';
        const params = new URLSearchParams({
          query: searchQuery,
          language,
          max_results: '20',
        });
        const res = await client.get(`/community-foods/search?${params}`);
        if (reqId === searchRef.current) {
          setFatsecretResults(res.data.results || []);
          setFatsecretLoading(false);
          setShowAutocomplete(false);
        }
      } catch { if (reqId === searchRef.current) setFatsecretLoading(false); }
    }, 600);
    return () => clearTimeout(timer);
  }, [searchQuery, i18n?.language, activeTab]);

  // Recipe search (600ms debounce)
  const recipeRef = useRef(0);
  useEffect(() => {
    if (activeTab !== 'recipes' || !recipeQuery || recipeQuery.length < 2) {
      setRecipeResults([]); setRecipeLoading(false); return;
    }
    const reqId = ++recipeRef.current;
    setRecipeLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await client.get(`/recipes/search?query=${encodeURIComponent(recipeQuery)}&max_results=10`);
        if (reqId === recipeRef.current) { setRecipeResults(res.data.recipes || []); setRecipeLoading(false); }
      } catch { if (reqId === recipeRef.current) setRecipeLoading(false); }
    }, 600);
    return () => clearTimeout(timer);
  }, [recipeQuery, activeTab]);

  const pickAutocomplete = (s) => { setSearchQuery(s); setShowAutocomplete(false); };

  const clearSelection = () => {
    setSelectedFood(null); setSelectedServing(null); setFatsecretDetail(null); setQuantity(1);
  };

  // Select food from community search (matches DietPage)
  const selectFatsecretFood = (food) => {
    setFatsecretDetail(null);
    setSelectedServing(null);
    setSelectedFood({
      id: food.id,
      _id: food.id,
      name: food.name,
      brand: food.brand,
      calories: food.calories,
      protein: food.protein,
      carbs: food.carbs,
      fat: food.fat,
      saturated_fat: food.saturated_fat,
      serving_size: food.serving_size || 100,
      serving_unit: food.serving_unit || 'g',
      serving_description: food.serving_description,
      servings: food.servings || [],
      source: 'community',
    });
    setQuantity(1);
  };

  // Select from history
  const selectHistoryFood = (rf) => {
    if (selectedFood?.name === rf.food_name && selectedFood?._fromHistory) { clearSelection(); return; }
    const servings = rf.servings || 1;
    setSelectedFood({
      id: rf.food_id, name: rf.food_name, _fromHistory: true,
      calories: Math.round(rf.calories / servings), protein: +(rf.protein / servings).toFixed(1),
      carbs: +(rf.carbs / servings).toFixed(1), fat: +(rf.fat / servings).toFixed(1),
      serving_size: rf.serving_size || 100, serving_unit: rf.serving_unit || 'g',
      serving_description: `${rf.serving_size || 100}${rf.serving_unit || 'g'}`,
    });
    setFatsecretDetail(null); setSelectedServing(null); setQuantity(1);
  };

  // Select from My Foods
  const selectMyFood = (food) => {
    if (selectedFood?.id === food.id && !selectedFood?._fromHistory) { clearSelection(); return; }
    setSelectedFood(food); setFatsecretDetail(null); setSelectedServing(null); setQuantity(1);
  };

  // Photo scan
  const handlePhotoScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoLoading(true); setPhotoResult(null); setPhotoFoods([]);
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await client.post('/foods/fatsecret/image-recognition', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const data = res.data;
      setPhotoResult(data);
      setPhotoFoods(data.foods || []);
    } catch (err) {
      toast.error(t('dietSuggestion.photoScanFailed'));
    }
    setPhotoLoading(false);
    e.target.value = '';
  };

  const selectPhotoFood = (food) => {
    setSelectedFood({
      id: food.fatsecret_id || food.food_id, name: food.food_entry_name || food.food_name,
      calories: food.calories || 0, protein: food.protein || 0,
      carbs: food.carbs || 0, fat: food.fat || 0,
      serving_size: food.eaten?.total_metric_amount || 100, serving_unit: food.eaten?.metric_description || 'g',
      serving_description: `${Math.round(food.eaten?.total_metric_amount || 100)}${food.eaten?.metric_description || 'g'}`,
    });
    setFatsecretDetail(null); setSelectedServing(null); setQuantity(1);
  };

  const handleServingChange = (servingId) => {
    const serving = fatsecretDetail?.servings?.find(s => s.serving_id === servingId)
      || selectedFood?.servings?.find(s => s.serving_id === servingId);
    if (serving) {
      setSelectedServing(serving);
      setSelectedFood(prev => prev ? {
        ...prev,
        serving_size: serving.metric_amount || serving.amount,
        serving_unit: serving.metric_unit || serving.unit,
        serving_description: serving.description,
        calories: serving.calories,
        protein: serving.protein,
        carbs: serving.carbs,
        fat: serving.fat,
        saturated_fat: serving.saturated_fat,
        fiber: serving.fiber,
        sugar: serving.sugar,
        sodium: serving.sodium,
        potassium: serving.potassium,
      } : prev);
      setQuantity(1);
    }
  };

  const addFoodToMeal = () => {
    if (!selectedFood) return;
    const safeQty = parseFloat(quantity) || 1;
    const servingSize = selectedServing?.metric_amount || selectedFood.serving_size || 100;
    const servingUnit = selectedServing?.metric_unit || selectedFood.serving_unit || 'g';
    const rawServings = (fatsecretDetail?.servings || selectedFood.servings || []);
    const foodServings = Array.isArray(rawServings) ? rawServings.filter(sv => sv && sv.serving_id).map(sv => ({
      serving_id: sv.serving_id,
      description: sv.description,
      metric_amount: sv.metric_amount,
      metric_unit: sv.metric_unit,
      calories: sv.calories,
      protein: sv.protein,
      carbs: sv.carbs,
      fat: sv.fat,
    })) : [];
    const food = {
      food_id: selectedFood.id, food_name: selectedFood.name,
      quantity: servingSize * safeQty, unit: servingUnit,
      serving_size: servingSize, serving_unit: servingUnit, servings: safeQty,
      serving_description: selectedServing?.description || selectedFood.serving_description || '',
      calories: selectedFood.calories, protein: selectedFood.protein,
      carbs: selectedFood.carbs, fat: selectedFood.fat,
      food_servings: foodServings,
      selected_serving_id: selectedServing?.serving_id || '',
    };
    touchAndSetForm(prev => ({
      ...prev, meals: prev.meals.map((m, i) => {
        if (i !== suggestionMealIdx) return m;
        if (replacingFoodIdx !== null) {
          const newFoods = [...m.foods];
          newFoods[replacingFoodIdx] = food;
          return { ...m, foods: newFoods };
        }
        return { ...m, foods: [...m.foods, food] };
      })
    }));
    setReplacingFoodIdx(null);
    clearSelection(); setSearchQuery(''); setFatsecretResults([]);
  };

  const selectRecipe = async (recipe) => {
    try {
      const res = await client.get(`/recipes/${recipe.recipe_id}`);
      setRecipeDetail(res.data); setSelectedRecipe(recipe);
    } catch { toast.error(t('dietSuggestion.failedLoadRecipe')); }
  };

  const addRecipeToMeal = () => {
    if (!recipeDetail) return;
    const n = recipeDetail.serving_sizes?.serving || {};
    const food = {
      food_id: `recipe-${recipeDetail.recipe_id}`, food_name: recipeDetail.recipe_name || selectedRecipe?.recipe_name,
      quantity: 1, unit: 'serving', serving_size: 1, serving_unit: 'serving', servings: 1,
      serving_description: n.serving_size || '1 serving',
      calories: parseFloat(n.calories || 0), protein: parseFloat(n.protein || 0),
      carbs: parseFloat(n.carbohydrate || 0), fat: parseFloat(n.fat || 0),
    };
    touchAndSetForm(prev => ({
      ...prev, meals: prev.meals.map((m, i) => i === suggestionMealIdx ? { ...m, foods: [...m.foods, food] } : m)
    }));
    setSelectedRecipe(null); setRecipeDetail(null); setRecipeQuery(''); setRecipeResults([]);
  };

  const addQuickMealToCurrentMeal = (qm) => {
    const newFoods = qm.foods.map(f => {
      const qty = f.quantity || 1;
      return {
        food_id: f.food_id || f.id, food_name: f.food_name || f.name,
        quantity: qty, unit: f.unit || 'g', serving_size: qty, serving_unit: f.unit || 'g', servings: 1,
        serving_description: `${qty}${f.unit || 'g'}`,
        calories: Math.round(f.calories * qty), protein: Math.round(f.protein * qty * 10) / 10,
        carbs: Math.round(f.carbs * qty * 10) / 10, fat: Math.round(f.fat * qty * 10) / 10,
      };
    });
    touchAndSetForm(prev => ({
      ...prev, meals: prev.meals.map((m, i) => i === suggestionMealIdx ? { ...m, foods: [...m.foods, ...newFoods] } : m)
    }));
  };

  // Apply the target-picker action. targetMealIdx is an existing meal index, or
  // { newOptionOf: mealType } to create a fresh option under that meal type.
  const applyTargetPicker = (target) => {
    if (!targetPicker) return;
    const { kind, mode, sourceMealIdx, sourceFoodIdx } = targetPicker;
    const letters = 'ABCDEFGHIJ';

    touchAndSetForm(prev => {
      let meals = prev.meals.map(m => ({ ...m, foods: [...m.foods] }));

      // Resolve target → actual index (creating a new option if needed)
      let destIdx;
      if (typeof target === 'number') {
        destIdx = target;
      } else if (target && target.newOptionOf) {
        const mt = target.newOptionOf;
        const siblings = meals.map((m, i) => ({ m, i })).filter(({ m }) => m.meal_type === mt);
        meals = meals.map((m, i) => {
          const sibIdx = siblings.findIndex(s => s.i === i);
          if (sibIdx === -1) return m;
          return { ...m, option_name: m.option_name || `Option ${letters[sibIdx]}` };
        });
        const newOption = {
          meal_type: mt,
          option_name: `Option ${letters[siblings.length]}`,
          foods: [],
          _tabId: Date.now() + Math.random(),
        };
        meals.push(newOption);
        destIdx = meals.length - 1;
      } else {
        return prev;
      }

      if (kind === 'food') {
        const food = meals[sourceMealIdx]?.foods?.[sourceFoodIdx];
        if (!food) return prev;
        meals[destIdx] = { ...meals[destIdx], foods: [...meals[destIdx].foods, { ...food }] };
        if (mode === 'move') {
          meals[sourceMealIdx] = {
            ...meals[sourceMealIdx],
            foods: meals[sourceMealIdx].foods.filter((_, fi) => fi !== sourceFoodIdx),
          };
          // Auto-delete the source option if it's now empty AND it's not the last option of its type
          if (meals[sourceMealIdx].foods.length === 0) {
            const mt = meals[sourceMealIdx].meal_type;
            const siblingsOfType = meals.filter(m => m.meal_type === mt);
            if (siblingsOfType.length > 1) {
              meals = meals.filter((_, i) => i !== sourceMealIdx);
              if (destIdx > sourceMealIdx) destIdx -= 1;
            }
          }
        }
      } else if (kind === 'option') {
        const sourceFoods = meals[sourceMealIdx]?.foods || [];
        meals[destIdx] = {
          ...meals[destIdx],
          foods: [...meals[destIdx].foods, ...sourceFoods.map(f => ({ ...f }))],
        };
      }

      setSuggestionMealIdx(destIdx);
      return { ...prev, meals };
    });

    toast.success(
      kind === 'option'
        ? t('dietSuggestion.duplicatedToast', 'Duplicated')
        : mode === 'move'
          ? t('dietSuggestion.movedToast', 'Moved')
          : t('dietSuggestion.copiedToast', 'Copied')
    );
    setTargetPicker(null);
    setReplacingFoodIdx(null);
  };

  const saveOptionAsQuickMeal = async (mealIdx) => {
    const meal = suggestionForm.meals[mealIdx];
    if (!meal || !meal.foods || meal.foods.length === 0) {
      toast.error(t('dietSuggestion.noFoodsYet'));
      return;
    }
    const defaultName = meal.option_name
      ? `${meal.meal_type} · ${meal.option_name}`
      : meal.meal_type;
    const name = await prompt({
      title: t('dietSuggestion.quickMealNamePrompt', 'Name this quick meal:'),
      placeholder: defaultName,
      defaultValue: defaultName,
      required: true,
    });
    if (!name || !name.trim()) return;
    try {
      const payload = {
        name: name.trim(),
        foods: meal.foods.map(f => ({
          food_id: f.food_id,
          food_name: f.food_name,
          quantity: (f.serving_size || 1) * (f.servings || 1),
          unit: f.serving_unit || 'g',
          calories: (f.calories || 0),
          protein: (f.protein || 0),
          carbs: (f.carbs || 0),
          fat: (f.fat || 0),
        })),
      };
      await client.post('/meal-templates', payload);
      const r = await client.get('/meal-templates');
      setQuickMeals(r.data);
      toast.success(t('dietSuggestion.quickMealSaved', 'Saved as quick meal'));
    } catch (err) {
      toast.error(t('dietSuggestion.failedSaveQuickMeal', 'Failed to save quick meal'));
    }
  };

  const addQuickMealAsNewOption = (qm) => {
    const currentMeal = suggestionForm.meals[suggestionMealIdx];
    if (!currentMeal) return;
    const mt = currentMeal.meal_type;
    const siblings = suggestionForm.meals.map((m, i) => ({ m, i })).filter(({ m }) => m.meal_type === mt);
    const letters = 'ABCDEFGHIJ';
    const newFoods = qm.foods.map(f => {
      const qty = f.quantity || 1;
      return {
        food_id: f.food_id || f.id, food_name: f.food_name || f.name,
        quantity: qty, unit: f.unit || 'g', serving_size: qty, serving_unit: f.unit || 'g', servings: 1,
        serving_description: `${qty}${f.unit || 'g'}`,
        calories: Math.round(f.calories * qty), protein: Math.round(f.protein * qty * 10) / 10,
        carbs: Math.round(f.carbs * qty * 10) / 10, fat: Math.round(f.fat * qty * 10) / 10,
      };
    });
    touchAndSetForm(prev => {
      const updated = prev.meals.map((m, i) => {
        const sibIdx = siblings.findIndex(s => s.i === i);
        if (sibIdx === -1) return m;
        return { ...m, option_name: m.option_name || `Option ${letters[sibIdx]}` };
      });
      const newOption = {
        meal_type: mt,
        option_name: `Option ${letters[siblings.length]}`,
        foods: newFoods,
        _tabId: Date.now() + Math.random(),
      };
      const newMeals = [...updated, newOption];
      setSuggestionMealIdx(newMeals.length - 1);
      return { ...prev, meals: newMeals };
    });
    toast.success(t('dietSuggestion.importedAsOption', 'Imported as new option'));
  };

  const removeFoodFromMeal = (mealIdx, foodIdx) => {
    touchAndSetForm(prev => ({
      ...prev, meals: prev.meals.map((m, i) => i === mealIdx ? { ...m, foods: m.foods.filter((_, fi) => fi !== foodIdx) } : m)
    }));
    if (mealIdx === suggestionMealIdx && replacingFoodIdx !== null) {
      if (foodIdx === replacingFoodIdx) setReplacingFoodIdx(null);
      else if (foodIdx < replacingFoodIdx) setReplacingFoodIdx(replacingFoodIdx - 1);
    }
  };

  const addOptionForCurrentMeal = () => {
    const currentMeal = suggestionForm.meals[suggestionMealIdx];
    if (!currentMeal) return;
    const siblings = suggestionForm.meals.map((m, i) => ({ m, i })).filter(({ m }) => m.meal_type === currentMeal.meal_type);
    const letters = 'ABCDEFGHIJ';
    const updatedMeals = suggestionForm.meals.map((m, i) => {
      const sibIdx = siblings.findIndex(s => s.i === i);
      if (sibIdx === -1) return m;
      return { ...m, option_name: m.option_name || `Option ${letters[sibIdx]}` };
    });
    const newOption = { meal_type: currentMeal.meal_type, option_name: `Option ${letters[siblings.length]}`, foods: [], _tabId: Date.now() };
    const newMeals = [...updatedMeals, newOption];
    touchAndSetForm(prev => ({ ...prev, meals: newMeals }));
    setSuggestionMealIdx(newMeals.length - 1);
    clearSelection(); setReplacingFoodIdx(null); setEditingServingIdx(null);
  };

  const updateFoodServings = (mealIdx, foodIdx, payload) => {
    const newServings = typeof payload === 'object' ? payload.servings : payload;
    const newServingId = typeof payload === 'object' ? payload.servingId : null;
    touchAndSetForm(prev => ({
      ...prev, meals: prev.meals.map((m, i) => {
        if (i !== mealIdx) return m;
        const newFoods = m.foods.map((f, fi) => {
          if (fi !== foodIdx) return f;
          let next = { ...f, servings: newServings };
          if (newServingId && newServingId !== f.selected_serving_id) {
            const picked = (f.food_servings || []).find(sv => sv.serving_id === newServingId);
            if (picked) {
              next = {
                ...next,
                selected_serving_id: picked.serving_id,
                serving_size: picked.metric_amount || next.serving_size,
                serving_unit: picked.metric_unit || next.serving_unit,
                serving_description: picked.description || next.serving_description,
                calories: picked.calories ?? next.calories,
                protein: picked.protein ?? next.protein,
                carbs: picked.carbs ?? next.carbs,
                fat: picked.fat ?? next.fat,
              };
            }
          }
          next.quantity = (next.serving_size || 1) * newServings;
          return next;
        });
        return { ...m, foods: newFoods };
      })
    }));
    setEditingServingIdx(null);
  };

  const submitDietSuggestion = async () => {
    const nonEmptyMeals = suggestionForm.meals.filter(m => m.foods.length > 0);
    if (nonEmptyMeals.length === 0) { toast.error(t('dietSuggestion.pleaseAddFood')); return; }
    const mealNames = [...new Set(nonEmptyMeals.map(m => m.meal_type))].map(mt => mt.charAt(0).toUpperCase() + mt.slice(1));
    const autoName = `Diet Plan (${mealNames.join(', ')})`;
    const name = editingSuggestion ? (suggestionName.trim() || autoName) : autoName;
    try {
      if (editingSuggestion) {
        await client.put(`/diet-suggestions/${editingSuggestion.id}`, {
          ...suggestionForm, name, meals: nonEmptyMeals, days_of_week: daysOfWeek
        });
        toast.success(t('dietSuggestion.updatedToast'));
      } else {
        await client.post('/diet-suggestions', {
          ...suggestionForm, name, meals: nonEmptyMeals, days_of_week: daysOfWeek
        });
        toast.success(t('dietSuggestion.sentToast'));
      }
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(e => e.msg || JSON.stringify(e)).join('; ')
        : (typeof detail === 'string' ? detail : t('dietSuggestion.failedSaveSuggestion'));
      toast.error(msg);
    }
  };

  // Group meals by meal_type, compute per-option totals, then min/max range
  const mealsByType = {};
  suggestionForm.meals.forEach(m => {
    const mt = (m.meal_type || 'other').toLowerCase();
    if (!mealsByType[mt]) mealsByType[mt] = [];
    const optTotals = m.foods.reduce((acc, f) => {
      const s = f.servings || 1;
      return { cal: acc.cal + f.calories * s, pro: acc.pro + f.protein * s, carb: acc.carb + f.carbs * s, fat: acc.fat + f.fat * s };
    }, { cal: 0, pro: 0, carb: 0, fat: 0 });
    mealsByType[mt].push(optTotals);
  });
  let totalCalMin = 0, totalCalMax = 0, totalProMin = 0, totalProMax = 0;
  let totalCarbMin = 0, totalCarbMax = 0, totalFatMin = 0, totalFatMax = 0;
  Object.values(mealsByType).forEach(opts => {
    totalCalMin += Math.min(...opts.map(o => o.cal));
    totalCalMax += Math.max(...opts.map(o => o.cal));
    totalProMin += Math.min(...opts.map(o => o.pro));
    totalProMax += Math.max(...opts.map(o => o.pro));
    totalCarbMin += Math.min(...opts.map(o => o.carb));
    totalCarbMax += Math.max(...opts.map(o => o.carb));
    totalFatMin += Math.min(...opts.map(o => o.fat));
    totalFatMax += Math.max(...opts.map(o => o.fat));
  });
  const isRange = Math.round(totalCalMin) !== Math.round(totalCalMax);
  const allFoods = suggestionForm.meals.flatMap(m => m.foods);

  const searchPlaceholder = activeTab === 'recipes' ? t('dietSuggestion.searchRecipesPlaceholder')
    : activeTab === 'history' || activeTab === 'myfoods' ? t('dietSuggestion.filterFoodsPlaceholder')
    : t('dietSuggestion.searchFoodPlaceholder');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleCloseAttempt(); else onOpenChange(true); }}>
      <DialogContent className="fixed inset-0 max-w-full w-full h-full max-h-full rounded-none m-0 p-0 flex flex-col translate-x-0 translate-y-0 [&>button]:hidden" data-testid="create-diet-suggestion-dialog">
        <DialogTitle className="sr-only">
          {editingSuggestion ? t('dietSuggestion.editDietSuggestion') : t('clients.createDietSuggestionTitle')}
        </DialogTitle>

        {/* Header */}
        <div className="flex items-start gap-3 px-4 pt-4 pb-3 border-b border-border/20 shrink-0">
          <UITooltip>
            <TooltipTrigger asChild>
              <button type="button" onClick={handleCloseAttempt} className="mt-0.5 p-1 rounded hover:bg-muted active:bg-muted transition-colors shrink-0">
                <ArrowLeft className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('tooltips.back')}</TooltipContent>
          </UITooltip>
          <div className="flex-1 min-w-0">
            <h2 style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-lg font-bold leading-tight">
              {editingSuggestion ? t('dietSuggestion.editDietSuggestion') : t('clients.createDietSuggestionTitle')}
            </h2>
            <p className="text-sm text-muted-foreground">{t('clients.forClient')} {clientName}</p>
            {editingSuggestion && (
              <input
                value={suggestionName}
                onChange={e => touchAndSetName(e.target.value)}
                placeholder={t('dietSuggestion.suggestionNamePlaceholder')}
                className="mt-1 w-full bg-transparent border-0 border-b border-border/40 focus:border-primary outline-none text-sm font-medium pb-0.5 transition-colors"
              />
            )}
          </div>
          {/* Days picker — header right */}
          <div className="shrink-0 text-right">
            <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{t('dietSuggestion.days')}</p>
            <div className="flex flex-wrap gap-1 justify-end max-w-[224px] md:max-w-[160px]">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                <button
                  key={day}
                  type="button"
                  onClick={() => touchAndSetDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])}
                  className={`min-h-[28px] md:min-h-0 min-w-[28px] md:min-w-0 px-2 py-1 md:px-1.5 md:py-0.5 rounded text-xs font-medium transition-colors active:scale-95 ${
                    daysOfWeek.includes(day) ? 'bg-primary text-white' : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Overlap warning */}
        {(() => {
          const existingDays = new Set();
          existingSuggestions
            .filter(s => !editingSuggestion || s.id !== editingSuggestion.id)
            .forEach(s => (s.days_of_week || []).forEach(d => existingDays.add(d)));
          const overlapping = daysOfWeek.filter(d => existingDays.has(d));
          if (overlapping.length === 0) return null;
          const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
          return (
            <div className="mx-4 mt-2 flex items-start gap-2 p-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
              <p className="text-xs text-yellow-500">
                {overlapping.map(d => DAY_LABELS[d]).join(', ')} already {overlapping.length === 1 ? 'has' : 'have'} a diet suggestion. The client will see multiple plans for {overlapping.length === 1 ? 'that day' : 'those days'}.
              </p>
            </div>
          );
        })()}

        {/* Compact top bar: meal type tabs + inline totals + collapsible client targets */}
        <div className="px-4 pt-2 pb-2 shrink-0 space-y-1.5">
          {/* Meal type tabs */}
          <div className="flex flex-wrap gap-2">
            {(() => {
              const seen = new Set();
              const uniqueTypes = [];
              suggestionForm.meals.forEach(m => { if (!seen.has(m.meal_type)) { seen.add(m.meal_type); uniqueTypes.push(m.meal_type); } });
              const activeMealType = suggestionForm.meals[suggestionMealIdx]?.meal_type;
              return uniqueTypes.map(mealType => {
                const isActive = mealType === activeMealType;
                const mealsOfType = suggestionForm.meals.map((m, i) => ({ m, i })).filter(({ m }) => m.meal_type === mealType);
                const typeKcal = mealsOfType.reduce((acc, { m }) => {
                  const optKcal = (m.foods || []).reduce((s, f) => s + (f.calories || 0) * (f.servings || 1), 0);
                  return acc + optKcal;
                }, 0) / Math.max(mealsOfType.length, 1);
                const mealTypeKey = mealType.toLowerCase();
                const baseLabel = ['breakfast', 'lunch', 'dinner', 'snack'].includes(mealTypeKey)
                  ? t(`diet.${mealTypeKey === 'snack' ? 'snacks' : mealTypeKey}`)
                  : mealType.charAt(0).toUpperCase() + mealType.slice(1);
                return (
                  <div
                    key={mealsOfType[0]?.m._tabId ?? mealType}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      isActive ? 'bg-primary text-white shadow-sm shadow-primary/30' : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {isActive ? (
                      <input
                        value={mealType}
                        onChange={e => {
                          const val = e.target.value;
                          touchAndSetForm(prev => ({ ...prev, meals: prev.meals.map(m => m.meal_type === mealType ? { ...m, meal_type: val } : m) }));
                        }}
                        onBlur={e => {
                          const trimmed = e.target.value.trim() || 'meal';
                          touchAndSetForm(prev => ({ ...prev, meals: prev.meals.map(m => m.meal_type === mealType ? { ...m, meal_type: trimmed } : m) }));
                        }}
                        className="bg-transparent border-0 outline-none focus:underline text-xs font-medium text-white w-20 min-w-0"
                        onClick={e => e.stopPropagation()}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => { setSuggestionMealIdx(mealsOfType[0]?.i ?? 0); clearSelection(); setReplacingFoodIdx(null); setEditingServingIdx(null); }}
                        className="bg-transparent border-0 outline-none"
                      >
                        {baseLabel}
                        {typeKcal > 0 && <span className="ml-1 opacity-70">· {Math.round(typeKcal)} kcal</span>}
                      </button>
                    )}
                    {uniqueTypes.length > 1 && (
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          const indicesToRemove = new Set(mealsOfType.map(({ i }) => i));
                          const newMeals = suggestionForm.meals.filter((_, i) => !indicesToRemove.has(i));
                          touchAndSetForm(prev => ({ ...prev, meals: newMeals }));
                          setSuggestionMealIdx(0); setReplacingFoodIdx(null);
                        }}
                        className="ml-0.5 opacity-70 hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                );
              });
            })()}
            <button
              type="button"
              onClick={() => {
                const newIdx = suggestionForm.meals.length;
                touchAndSetForm(prev => ({
                  ...prev,
                  meals: [...prev.meals, { meal_type: `meal-${prev.meals.length + 1}`, option_name: null, foods: [], _tabId: Date.now() }]
                }));
                setSuggestionMealIdx(newIdx);
              }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-muted/50 hover:bg-muted active:bg-muted border border-dashed border-border/50 text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <Plus className="h-3 w-3" /> {t('dietSuggestion.newMeal')}
            </button>
          </div>

          {/* Inline DAILY totals + client targets toggle */}
          {(allFoods.length > 0 || clientTargets) && (
            <div className="flex items-center gap-3 flex-wrap text-xs">
              {allFoods.length > 0 && (
                <>
                  <span className="text-muted-foreground uppercase tracking-wide">{t('dietSuggestion.daily')}</span>
                  <span className="text-primary font-bold text-sm">
                    {isRange ? `${Math.round(totalCalMin)}–${Math.round(totalCalMax)}` : Math.round(totalCalMax)} kcal
                  </span>
                  <span className="text-muted-foreground">P <span className="text-foreground font-medium">
                    {isRange ? `${Math.round(totalProMin)}–${Math.round(totalProMax)}` : Math.round(totalProMax)}g
                  </span></span>
                  <span className="text-muted-foreground">C <span className="text-foreground font-medium">
                    {isRange ? `${Math.round(totalCarbMin)}–${Math.round(totalCarbMax)}` : Math.round(totalCarbMax)}g
                  </span></span>
                  <span className="text-muted-foreground">F <span className="text-foreground font-medium">
                    {isRange ? `${Math.round(totalFatMin)}–${Math.round(totalFatMax)}` : Math.round(totalFatMax)}g
                  </span></span>
                </>
              )}
              {clientTargets && (
                <button
                  type="button"
                  onClick={() => setTargetsExpanded(e => !e)}
                  className="ml-auto flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
                  data-testid="toggle-client-targets"
                >
                  {targetsExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  {t('dietSuggestion.clientTargets', "Client's targets")}
                </button>
              )}
            </div>
          )}

          {/* Collapsible client targets panel */}
          {targetsExpanded && clientTargets && (() => {
            const flat = {
              calories: clientTargets.target_calories || 0,
              protein: clientTargets.target_protein || 0,
              carbs: clientTargets.target_carbs || 0,
              fat: clientTargets.target_fat || 0,
            };
            const dmt = clientTargets.daily_macro_targets || null;
            const hasFlat = flat.calories || flat.protein || flat.carbs || flat.fat;
            const hasPerDay = dmt && Object.keys(dmt).length > 0;
            if (!hasFlat && !hasPerDay) return null;
            const fmt = (tg) => `${Math.round(tg.calories || 0)} kcal · P ${Math.round(tg.protein || 0)}g · C ${Math.round(tg.carbs || 0)}g · F ${Math.round(tg.fat || 0)}g`;
            const DAYS_ORDER = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
            const DAY_LABELS = {
              monday: t('common.daysShort.mon', 'Mon'),
              tuesday: t('common.daysShort.tue', 'Tue'),
              wednesday: t('common.daysShort.wed', 'Wed'),
              thursday: t('common.daysShort.thu', 'Thu'),
              friday: t('common.daysShort.fri', 'Fri'),
              saturday: t('common.daysShort.sat', 'Sat'),
              sunday: t('common.daysShort.sun', 'Sun'),
            };
            if (!hasPerDay) {
              return (
                <div className="rounded-lg bg-muted/30 px-3 py-2 text-xs">
                  <span className="text-foreground/80">{fmt(flat)}</span>
                </div>
              );
            }
            const visibleDays = (daysOfWeek && daysOfWeek.length > 0)
              ? DAYS_ORDER.filter(d => daysOfWeek.includes(d))
              : DAYS_ORDER;
            return (
              <div className="rounded-lg bg-muted/30 px-3 py-2 text-xs space-y-0.5">
                {visibleDays.map(d => {
                  const dt = dmt[d] || flat;
                  return (
                    <div key={d} className="flex items-center gap-2">
                      <span className="inline-block w-9 text-muted-foreground">{DAY_LABELS[d]}</span>
                      <span className="text-foreground/80">{fmt(dt)}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Meal Card header: active meal title + option chips + kebab (sticky) */}
        <div className="mx-4 mt-1 px-3 py-2 rounded-t-xl bg-card/60 border border-b-0 border-border/40 shrink-0">
          {(() => {
            const activeMeal = suggestionForm.meals[suggestionMealIdx];
            if (!activeMeal) return null;
            const activeMealType = activeMeal.meal_type;
            const siblings = suggestionForm.meals.map((m, i) => ({ m, i })).filter(({ m }) => m.meal_type === activeMealType);
            const mealTypeKey = activeMealType.toLowerCase();
            const mealTypeLabel = ['breakfast', 'lunch', 'dinner', 'snack'].includes(mealTypeKey)
              ? t(`diet.${mealTypeKey === 'snack' ? 'snacks' : mealTypeKey}`)
              : activeMealType.charAt(0).toUpperCase() + activeMealType.slice(1);
            const optionPalette = [
              { active: 'bg-orange-500/20 text-orange-400 border-orange-500/40', idle: 'bg-orange-500/5 hover:bg-orange-500/10 text-orange-400/70 border-orange-500/20' },
              { active: 'bg-sky-500/20 text-sky-400 border-sky-500/40', idle: 'bg-sky-500/5 hover:bg-sky-500/10 text-sky-400/70 border-sky-500/20' },
              { active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40', idle: 'bg-emerald-500/5 hover:bg-emerald-500/10 text-emerald-400/70 border-emerald-500/20' },
              { active: 'bg-violet-500/20 text-violet-400 border-violet-500/40', idle: 'bg-violet-500/5 hover:bg-violet-500/10 text-violet-400/70 border-violet-500/20' },
              { active: 'bg-rose-500/20 text-rose-400 border-rose-500/40', idle: 'bg-rose-500/5 hover:bg-rose-500/10 text-rose-400/70 border-rose-500/20' },
            ];
            const optionKcal = (m) => (m.foods || []).reduce((s, f) => s + (f.calories || 0) * (f.servings || 1), 0);
            const hasAnyFoods = (activeMeal.foods || []).length > 0;

            return (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs font-semibold text-foreground/90 mr-1">{mealTypeLabel}</span>
                {siblings.length > 1 ? (
                  <>
                    {siblings.map(({ m, i }, sIdx) => {
                      const isActive = i === suggestionMealIdx;
                      const palette = optionPalette[sIdx % optionPalette.length];
                      return (
                        <button
                          key={m._tabId}
                          type="button"
                          onClick={() => { setSuggestionMealIdx(i); clearSelection(); setReplacingFoodIdx(null); setEditingServingIdx(null); }}
                          className={`flex items-center gap-1 min-h-[36px] md:min-h-0 px-2.5 py-1.5 md:py-0.5 rounded text-xs font-medium transition-colors border active:scale-95 ${
                            isActive ? palette.active : palette.idle
                          }`}
                          data-testid={`option-chip-${i}`}
                        >
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${isActive ? 'bg-current' : 'bg-current opacity-40'}`} />
                          <span>{m.option_name || t('dietSuggestion.single', 'Single')}</span>
                          {optionKcal(m) > 0 && <span className="opacity-70">· {Math.round(optionKcal(m))} kcal</span>}
                        </button>
                      );
                    })}
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {hasAnyFoods ? `${Math.round(optionKcal(activeMeal))} kcal` : t('dietSuggestion.empty', 'empty')}
                  </span>
                )}
                <button
                  type="button"
                  onClick={addOptionForCurrentMeal}
                  className="flex items-center gap-0.5 min-h-[36px] md:min-h-0 px-2.5 py-1.5 md:py-0.5 rounded text-xs text-muted-foreground hover:text-primary active:scale-95 transition-colors border border-dashed border-border/40"
                  data-testid="add-option-btn"
                >
                  <Plus className="h-2.5 w-2.5" /> {t('dietSuggestion.option')}
                </button>
                <div className="ml-auto flex items-center gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-primary"
                    disabled={!hasAnyFoods}
                    tooltip={t('dietSuggestion.duplicateOption', 'Duplicate option')}
                    onClick={() => setTargetPicker({ kind: 'option', mode: 'duplicate', sourceMealIdx: suggestionMealIdx })}
                    data-testid="meal-duplicate-option-btn"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-primary"
                    disabled={!hasAnyFoods}
                    tooltip={t('dietSuggestion.saveAsQuickMeal', 'Save as Quick Meal')}
                    onClick={() => saveOptionAsQuickMeal(suggestionMealIdx)}
                    data-testid="meal-save-quick-meal-btn"
                  >
                    <Save className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    disabled={siblings.length <= 1}
                    tooltip={t('dietSuggestion.deleteOption', 'Delete option')}
                    onClick={() => {
                      const currentIdx = suggestionMealIdx;
                      const mealType = suggestionForm.meals[currentIdx]?.meal_type;
                      const newMeals = suggestionForm.meals.filter((_, idx2) => idx2 !== currentIdx);
                      const nextIdx = newMeals.findIndex(m => m.meal_type === mealType);
                      touchAndSetForm(prev => ({ ...prev, meals: newMeals }));
                      setSuggestionMealIdx(nextIdx >= 0 ? nextIdx : 0);
                      setReplacingFoodIdx(null);
                    }}
                    data-testid="meal-delete-option-btn"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Meal Card body: search + tabs + results (scrollable middle of card) */}
        <div className="flex-1 overflow-y-auto mx-4 px-3 pb-3 space-y-3 pt-3 bg-card/40 border-x border-border/40">

          {/* Replacing Banner */}
          {replacingFoodIdx !== null && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/30 text-sm">
              <span className="flex-1 text-primary font-medium truncate">
                {t('dietSuggestion.replacing')} {suggestionForm.meals[suggestionMealIdx]?.foods[replacingFoodIdx]?.food_name}
              </span>
              <button onClick={() => setReplacingFoodIdx(null)}>
                <X className="h-4 w-4 text-primary" />
              </button>
            </div>
          )}

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={activeTab === 'recipes' ? recipeQuery : searchQuery}
              onChange={(e) => {
                if (activeTab === 'recipes') setRecipeQuery(e.target.value);
                else setSearchQuery(e.target.value);
                if (activeTab === 'photo' || activeTab === 'scan' || activeTab === 'quick') setActiveTab('search');
              }}
              onFocus={() => { if (activeTab === 'photo' || activeTab === 'scan' || activeTab === 'quick') setActiveTab('search'); }}
              placeholder={searchPlaceholder}
              className="pl-8 h-9 text-sm"
              data-testid="suggestion-food-search"
            />
            {(fatsecretLoading || recipeLoading) && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>

          {/* Autocomplete pills */}
          {activeTab === 'search' && showAutocomplete && autocompleteResults.length > 0 && !selectedFood && (
            <div className="flex flex-wrap gap-1">
              {autocompleteResults.map((s, i) => (
                <button key={i} onClick={() => pickAutocomplete(s)} className="px-2 py-1 rounded-full bg-muted/60 hover:bg-primary/90 text-xs transition-colors">{s}</button>
              ))}
            </div>
          )}

          {/* 6-Tab Bar */}
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedFood(null); setSelectedServing(null); setFatsecretDetail(null); setQuantity(1); setSelectedRecipe(null); setRecipeDetail(null); setPhotoResult(null); setPhotoFoods([]); }} className="w-full">
            <TabsList className="grid w-full grid-cols-6 h-8" data-testid="suggestion-tabs">
              <TabsTrigger value="history" className="text-xs px-0.5 gap-0.5"><Clock className="h-3.5 w-3.5" /> History</TabsTrigger>
              <TabsTrigger value="myfoods" className="text-xs px-0.5 gap-0.5"><UtensilsCrossed className="h-3.5 w-3.5" /> My Foods</TabsTrigger>
              <TabsTrigger value="photo" className="text-xs px-0.5 gap-0.5"><Camera className="h-3.5 w-3.5" /> Photo</TabsTrigger>
              <TabsTrigger value="scan" className="text-xs px-0.5 gap-0.5"><Scan className="h-3.5 w-3.5" /> Scan</TabsTrigger>
              <TabsTrigger value="recipes" className="text-xs px-0.5 gap-0.5"><BookOpen className="h-3.5 w-3.5" /> Recipes</TabsTrigger>
              <TabsTrigger value="quick" className="text-xs px-0.5 gap-0.5"><Zap className="h-3.5 w-3.5" /> Quick</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* ---- HISTORY TAB ---- */}
          {activeTab === 'history' && (
            <div className="space-y-1">
              {recentLoading && <div className="flex items-center justify-center py-4 gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('dietSuggestion.loading')}</div>}
              {!recentLoading && recentFoods.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-25" />
                  <p className="text-sm font-medium">{t('dietSuggestion.noRecentFoods')}</p>
                  <p className="text-xs mt-1">{t('dietSuggestion.recentFoodsHint')}</p>
                </div>
              )}
              {recentFoods.filter(rf => !searchQuery || searchQuery.length < 2 || rf.food_name.toLowerCase().includes(searchQuery.toLowerCase())).map((rf, idx) => (
                <div key={`${rf.food_name}-${idx}`}
                  className={`p-2.5 rounded-lg cursor-pointer transition-colors ${selectedFood?.name === rf.food_name && selectedFood?._fromHistory ? 'bg-primary/10 border border-primary' : 'bg-muted/30 hover:bg-muted/60'}`}
                  onClick={() => selectHistoryFood(rf)}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{rf.food_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {rf.serving_size || 100}{rf.serving_unit || 'g'} per serving · {Math.round(rf.calories)} kcal · P:{Math.round(rf.protein)}g C:{Math.round(rf.carbs)}g F:{Math.round(rf.fat)}g
                      </p>
                    </div>
                    <Badge variant="outline" className="text-[9px] h-5 shrink-0 ml-2">x{rf.log_count}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ---- MY FOODS TAB ---- */}
          {activeTab === 'myfoods' && (
            <div className="space-y-1">
              {myFoods.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <UtensilsCrossed className="h-8 w-8 mx-auto mb-2 opacity-25" />
                  <p className="text-sm">{t('dietSuggestion.noFoodsInDatabase')}</p>
                  <p className="text-xs mt-1">{t('dietSuggestion.noFoodsInDatabaseHint')}</p>
                </div>
              )}
              {myFoods.filter(f => !searchQuery || searchQuery.length < 2 || f.name?.toLowerCase().includes(searchQuery.toLowerCase())).map(food => (
                <div key={food.id}
                  className={`p-2.5 rounded-lg cursor-pointer transition-colors ${selectedFood?.id === food.id && !selectedFood?._fromHistory ? 'bg-primary/10 border border-primary' : 'bg-muted/30 hover:bg-muted/60'}`}
                  onClick={() => selectMyFood(food)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{food.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {food.serving_description || (food.serving_size != null && food.serving_unit ? `${food.serving_size}${food.serving_unit}` : 'serving')} · {food.calories} kcal · P:{food.protein}g C:{food.carbs}g F:{food.fat}g
                      </p>
                    </div>
                    {food.source === 'fatsecret' && <Badge className="bg-green-500/10 text-green-400 border-green-500/30 text-[9px]">FatSecret</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ---- SEARCH TAB ---- */}
          {activeTab === 'search' && (
            <div className="space-y-1">
              {searchQuery && searchQuery.length >= 2 ? (
                <>
                  {fatsecretResults.length > 0 && !selectedFood && (
                    <ScrollArea className="h-[200px] rounded border border-border/30">
                      <div className="p-1.5 space-y-1">
                        {fatsecretResults.map((food, idx) => (
                          <div
                            key={`community-${food.id}`}
                            className={`p-2 rounded-lg cursor-pointer transition-colors ${
                              idx === 0
                                ? 'bg-[#FF5C00]/10 border border-[#FF5C00]/30 hover:bg-[#FF5C00]/20'
                                : 'hover:bg-muted/60'
                            }`}
                            onClick={() => selectFatsecretFood(food)}
                            data-testid={`suggestion-food-${food.id}`}
                          >
                            <div className="flex items-center gap-2">
                              {idx === 0 && <Star className="h-3 w-3 text-[#FF5C00] fill-[#FF5C00] shrink-0" />}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">
                                  {food.brand ? <span className="text-[#FF5C00]">{food.brand}</span> : null}
                                  {food.brand ? ' ' : ''}{food.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {food.serving_description || `${food.serving_size || 100}${food.serving_unit || 'g'}`} · {Math.round(food.calories)} kcal · P:{food.protein}g C:{food.carbs}g F:{food.fat}g
                                </p>
                                {food.category && (
                                  <p className="text-[9px] text-muted-foreground/60">{food.category}</p>
                                )}
                              </div>
                              {food.brand ? (
                                <span className="text-[10px] px-1.5 py-0.5 rounded border border-[#FF5C00]/30 text-[#FF5C00] shrink-0">Brand</span>
                              ) : (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF5C00]/10 text-[#FF5C00] border border-[#FF5C00]/30 shrink-0">Generic</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                  {!fatsecretLoading && fatsecretResults.length === 0 && !selectedFood && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Search className="h-8 w-8 mx-auto mb-2 opacity-25" />
                      <p className="text-sm">{t('common.noResults')}</p>
                    </div>
                  )}
                </>
              ) : !selectedFood && (
                <div className="text-center py-8 text-muted-foreground">
                  <Search className="h-8 w-8 mx-auto mb-2 opacity-25" />
                  <p className="text-sm font-medium">{t('dietSuggestion.searchMillionsFoods')}</p>
                </div>
              )}
            </div>
          )}

          {/* ---- PHOTO TAB ---- */}
          {activeTab === 'photo' && (
            <div className="space-y-3">
              {!photoResult && !photoLoading && (
                <div className="flex flex-col items-center justify-center py-6 gap-3">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <Camera className="h-10 w-10 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground">{t('dietSuggestion.photoIdentifyHint')}</p>
                  <label htmlFor="suggestion-photo-input" className="cursor-pointer">
                    <div className="flex items-center gap-2 px-5 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium text-sm transition-colors">
                      <Camera className="h-4 w-4" /> {t('dietSuggestion.choosePhoto')}
                    </div>
                    <input id="suggestion-photo-input" type="file" accept="image/*" capture="environment" className="sr-only" onChange={handlePhotoScan} />
                  </label>
                </div>
              )}
              {photoLoading && (
                <div className="flex flex-col items-center py-8 gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm">{t('dietSuggestion.analyzingMeal')}</p>
                </div>
              )}
              {photoResult && !photoLoading && (
                <div className="space-y-2">
                  <p className="font-bold text-sm">{photoResult.food_count > 1 ? t('dietSuggestion.foodsDetectedPlural', { count: photoResult.food_count }) : t('dietSuggestion.foodsDetected', { count: photoResult.food_count })}</p>
                  <div className="grid grid-cols-4 gap-1.5 text-center">
                    <div className="rounded-lg p-2 bg-primary/10"><p className="text-sm font-bold text-primary">{Math.round(photoResult.total_calories)}</p><p className="text-[9px] text-muted-foreground">kcal</p></div>
                    <div className="rounded-lg p-2 bg-red-500/10"><p className="text-sm font-bold text-red-500">{Math.round(photoResult.total_protein)}g</p><p className="text-[9px] text-muted-foreground">protein</p></div>
                    <div className="rounded-lg p-2 bg-yellow-500/10"><p className="text-sm font-bold text-yellow-500">{Math.round(photoResult.total_carbs)}g</p><p className="text-[9px] text-muted-foreground">carbs</p></div>
                    <div className="rounded-lg p-2 bg-blue-500/10"><p className="text-sm font-bold text-blue-500">{Math.round(photoResult.total_fat)}g</p><p className="text-[9px] text-muted-foreground">fat</p></div>
                  </div>
                  {photoFoods.map((food, i) => (
                    <div key={i} className={`p-2 rounded-lg cursor-pointer transition-colors ${selectedFood?.id === food.fatsecret_id ? 'bg-primary/10 border border-primary' : 'bg-muted/30 hover:bg-muted/60'}`}
                      onClick={() => selectPhotoFood(food)}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">{food.food_entry_name || food.food_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {food.eaten?.total_metric_amount ? `${Math.round(food.eaten.total_metric_amount)}${food.eaten?.metric_description || 'g'}` : ''} · {Math.round(food.calories)} kcal
                          </p>
                        </div>
                        {selectedFood?.id === food.fatsecret_id && <Check className="h-4 w-4 text-primary" />}
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <label htmlFor="suggestion-photo-retry" className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-center gap-2 px-3 py-2 border rounded-lg text-sm hover:bg-muted active:bg-muted"><Camera className="h-4 w-4" /> {t('dietSuggestion.retake')}</div>
                      <input id="suggestion-photo-retry" type="file" accept="image/*" capture="environment" className="sr-only" onChange={handlePhotoScan} />
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- SCAN TAB ---- */}
          {activeTab === 'scan' && (
            <div className="flex flex-col items-center justify-center py-6 text-center space-y-3">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                <Scan className="h-10 w-10 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">{t('diet.scanBarcode')}</p>
                <p className="text-xs text-muted-foreground mt-1">{t('dietSuggestion.useBarcodeScanner')}</p>
              </div>
            </div>
          )}

          {/* ---- RECIPES TAB ---- */}
          {activeTab === 'recipes' && (
            <div className="space-y-2">
              {recipeResults.length > 0 && !selectedRecipe && (
                <ScrollArea className="h-[200px] rounded border border-border/30">
                  <div className="p-1.5 space-y-1">
                    {recipeResults.map(recipe => (
                      <div key={recipe.recipe_id} className="p-2 rounded-lg cursor-pointer hover:bg-muted/60"
                        onClick={() => selectRecipe(recipe)} data-testid={`suggestion-recipe-${recipe.recipe_id}`}>
                        <p className="font-medium text-sm">{recipe.recipe_name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{recipe.recipe_description}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
              {selectedRecipe && recipeDetail && (
                <div className="space-y-2 p-3 rounded-lg bg-primary/10 border border-primary/30">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-sm">{recipeDetail.recipe_name}</p>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <button onClick={() => { setSelectedRecipe(null); setRecipeDetail(null); }}><X className="h-4 w-4 text-muted-foreground" /></button>
                      </TooltipTrigger>
                      <TooltipContent>{t('tooltips.clearSelection')}</TooltipContent>
                    </UITooltip>
                  </div>
                  {recipeDetail.serving_sizes?.serving && (
                    <div className="flex gap-2 text-xs">
                      <span className="font-bold text-primary">{Math.round(parseFloat(recipeDetail.serving_sizes.serving.calories || 0))} kcal</span>
                      <span className="text-red-500">P: {Math.round(parseFloat(recipeDetail.serving_sizes.serving.protein || 0))}g</span>
                      <span className="text-yellow-500">C: {Math.round(parseFloat(recipeDetail.serving_sizes.serving.carbohydrate || 0))}g</span>
                      <span className="text-blue-500">F: {Math.round(parseFloat(recipeDetail.serving_sizes.serving.fat || 0))}g</span>
                    </div>
                  )}
                  <Button size="sm" className="h-9 md:h-7 bg-primary hover:bg-primary/90" onClick={addRecipeToMeal}><Plus className="h-3.5 w-3.5 mr-1" /> {t('dietSuggestion.addRecipe')}</Button>
                </div>
              )}
              {!recipeLoading && recipeQuery.length >= 2 && recipeResults.length === 0 && !selectedRecipe && (
                <p className="text-xs text-muted-foreground text-center py-4">{t('dietSuggestion.noRecipesFound')}</p>
              )}
            </div>
          )}

          {/* ---- QUICK MEALS TAB ---- */}
          {activeTab === 'quick' && (
            <div className="space-y-2">
              {quickMeals.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Zap className="h-8 w-8 mx-auto mb-2 opacity-25" />
                  <p className="text-sm">{t('dietSuggestion.noQuickMealsSaved')}</p>
                </div>
              ) : quickMeals.map(qm => (
                <div key={qm.id} className="p-3 rounded-lg bg-muted/30 transition-colors" data-testid={`quick-meal-${qm.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm flex items-center gap-1.5 truncate"><Bookmark className="h-3.5 w-3.5 text-primary shrink-0" />{qm.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{Math.round(qm.total_calories)} kcal · {qm.foods?.length || 0} foods</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => addQuickMealToCurrentMeal(qm)} data-testid={`quick-meal-add-${qm.id}`}>
                        <Plus className="h-3 w-3 mr-1" /> {t('dietSuggestion.addToCurrent', 'Add')}
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => addQuickMealAsNewOption(qm)} data-testid={`quick-meal-as-option-${qm.id}`}>
                        <Plus className="h-3 w-3 mr-1" /> {t('dietSuggestion.asNewOption', 'As option')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Selected Food — Serving Picker (works for search, history, myfoods, photo) */}
          {selectedFood && activeTab !== 'recipes' && activeTab !== 'quick' && (
            <div className="space-y-2 p-3 rounded-lg bg-primary/10 border border-primary/30">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-sm">{selectedFood.name}</p>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <button onClick={clearSelection}><X className="h-4 w-4 text-muted-foreground hover:text-foreground" /></button>
                  </TooltipTrigger>
                  <TooltipContent>{t('tooltips.clearSelection')}</TooltipContent>
                </UITooltip>
              </div>
              {selectedServing && (
                <p className="text-xs text-muted-foreground">
                  1 serving = {selectedServing.metric_serving_amount || selectedServing.metric_amount || 100}{selectedServing.metric_serving_unit || selectedServing.metric_unit || 'g'}
                </p>
              )}
              {selectedFood.servings?.length > 1 && (
                <div className="flex items-center gap-1.5">
                  <Select value={selectedServing?.serving_id || ''} onValueChange={handleServingChange} className="flex-1">
                    <SelectTrigger className="h-11 md:h-9 md:h-7 text-xs flex-1" data-testid="suggestion-serving-select"><SelectValue placeholder="Select serving..." /></SelectTrigger>
                    <SelectContent>
                      {selectedFood.servings.map(s => (<SelectItem key={s.serving_id} value={s.serving_id} className="text-xs">{s.description} ({s.calories} kcal)</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {selectedFood.servings.some(s => s.metric_amount === 100 && s.metric_unit === 'g') && (
                    <Button variant={selectedServing?.metric_amount === 100 && selectedServing?.metric_unit === 'g' ? 'default' : 'outline'} size="sm"
                      className={`h-9 md:h-7 px-2 text-xs shrink-0 ${selectedServing?.metric_amount === 100 && selectedServing?.metric_unit === 'g' ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
                      onClick={() => { const p = selectedFood.servings.find(s => s.metric_amount === 100 && s.metric_unit === 'g'); if (p) handleServingChange(p.serving_id); }}>
                      /100g
                    </Button>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="outline" size="icon" className="h-11 w-11 md:h-7 md:w-7" tooltip={t('tooltips.decreaseServings')} onClick={() => setQuantity(Math.max(0.5, (parseFloat(quantity) || 1) - 0.5))}>-</Button>
                  <input
                    type="number"
                    min="0.1"
                    step="0.5"
                    value={quantity}
                    onChange={e => {
                      const val = parseFloat(e.target.value);
                      if (!isNaN(val) && val > 0) setQuantity(val);
                      else if (e.target.value === '') setQuantity('');
                    }}
                    onBlur={e => {
                      const val = parseFloat(e.target.value);
                      if (isNaN(val) || val <= 0) setQuantity(1);
                    }}
                    className="w-14 text-center text-sm font-medium bg-transparent border border-input rounded-md px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <Button variant="outline" size="icon" className="h-11 w-11 md:h-7 md:w-7" tooltip={t('tooltips.increaseServings')} onClick={() => setQuantity(q => (parseFloat(q) || 1) + 0.5)}>+</Button>
                </div>
                <span className="text-xs text-muted-foreground shrink-0 bg-muted/50 px-2 py-0.5 rounded">
                  = {Math.round((selectedServing?.metric_serving_amount || selectedServing?.metric_amount || selectedFood.serving_size || 100) * (parseFloat(quantity) || 0) * 10) / 10}{selectedServing?.metric_serving_unit || selectedServing?.metric_unit || selectedFood.serving_unit || 'g'}
                </span>
                <Button size="sm" className="h-9 md:h-7 bg-primary hover:bg-primary/90 ml-auto" onClick={addFoodToMeal} data-testid="add-food-to-suggestion-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> {t('dietSuggestion.add')}
                </Button>
              </div>
              <div className="flex gap-2 text-xs justify-between">
                <span className="font-bold text-primary">{Math.round(selectedFood.calories * (parseFloat(quantity) || 0))} kcal</span>
                <span className="text-red-500">P: {(selectedFood.protein * (parseFloat(quantity) || 0)).toFixed(1)}g</span>
                <span className="text-yellow-500">C: {(selectedFood.carbs * (parseFloat(quantity) || 0)).toFixed(1)}g</span>
                <span className="text-blue-500">F: {(selectedFood.fat * (parseFloat(quantity) || 0)).toFixed(1)}g</span>
              </div>
            </div>
          )}

        </div>

        {/* Meal Card footer: foods list + notes */}
        <div className="mx-4 mb-2 px-3 py-2 rounded-b-xl bg-card/40 border border-t-0 border-border/40 shrink-0 space-y-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{t('dietSuggestion.foodsInOption', 'Foods in this option')}</p>
          <div className="rounded-lg border border-border/40 bg-background/50 overflow-hidden">
            {suggestionForm.meals[suggestionMealIdx]?.foods?.length > 0 ? (
              suggestionForm.meals[suggestionMealIdx].foods.map((food, fIdx) => (
                <SuggestionFoodRow
                  key={`${food.food_id}-${fIdx}`}
                  food={food}
                  isEditing={editingServingIdx === fIdx}
                  onEditServing={() => setEditingServingIdx(fIdx)}
                  onSaveServing={(newServings) => updateFoodServings(suggestionMealIdx, fIdx, newServings)}
                  onCancelServing={() => setEditingServingIdx(null)}
                  onRemove={() => removeFoodFromMeal(suggestionMealIdx, fIdx)}
                  onCopyMove={() => setTargetPicker({ kind: 'food', mode: 'copy', sourceMealIdx: suggestionMealIdx, sourceFoodIdx: fIdx })}
                />
              ))
            ) : (
              <div className="flex items-center justify-center py-2 text-muted-foreground gap-1.5">
                <UtensilsCrossed className="h-4 w-4 opacity-30" />
                <p className="text-xs">{t('dietSuggestion.noFoodsYet')}</p>
              </div>
            )}
          </div>
          {(() => {
            const foods = suggestionForm.meals[suggestionMealIdx]?.foods || [];
            if (foods.length === 0) return null;
            const totals = foods.reduce((acc, f) => {
              const s = f.servings || 1;
              return {
                kcal: acc.kcal + (f.calories || 0) * s,
                p: acc.p + (f.protein || 0) * s,
                c: acc.c + (f.carbs || 0) * s,
                fat: acc.fat + (f.fat || 0) * s,
              };
            }, { kcal: 0, p: 0, c: 0, fat: 0 });
            return (
              <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md bg-muted/30 border border-border/30 text-xs">
                <span className="font-bold text-primary">{Math.round(totals.kcal)} kcal</span>
                <div className="flex items-center gap-3">
                  <span className="text-red-500">P {totals.p.toFixed(1)}g</span>
                  <span className="text-yellow-500">C {totals.c.toFixed(1)}g</span>
                  <span className="text-blue-500">F {totals.fat.toFixed(1)}g</span>
                </div>
              </div>
            );
          })()}
          <Input value={suggestionForm.notes} onChange={(e) => touchAndSetForm(p => ({ ...p, notes: e.target.value }))}
            placeholder={t('clients.anyNotes', 'Notes (optional)...')} className="text-sm h-8" />
        </div>

        {/* Action buttons */}
        <div className="px-4 py-3 border-t border-border/20 flex items-center gap-2 shrink-0">
          <EditorAutosaveBar
            canUndo={canUndo} canRedo={canRedo} onUndo={undo} onRedo={redo}
            status={saveStatus} showStatus={!!editingSuggestion}
            className="flex-1 justify-start"
          />
          <Button variant="outline" onClick={handleCloseAttempt}>{editingSuggestion ? t('common.done', 'Done') : t('common.cancel')}</Button>
          {!editingSuggestion && (
            <Button onClick={submitDietSuggestion} className="bg-primary hover:bg-primary/90" data-testid="submit-diet-suggestion-btn">{t('clients.sendSuggestion')}</Button>
          )}
        </div>
      </DialogContent>

      {/* Target picker — copy/move food or duplicate option across meal types/options */}
      <Dialog open={!!targetPicker} onOpenChange={(o) => !o && setTargetPicker(null)}>
        <DialogContent className="max-w-[420px] rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base">
              {targetPicker?.kind === 'option'
                ? t('dietSuggestion.duplicateOptionTitle', 'Duplicate this option to…')
                : targetPicker?.mode === 'move'
                  ? t('dietSuggestion.moveFoodTitle', 'Move food to…')
                  : t('dietSuggestion.copyFoodTitle', 'Copy food to…')}
            </DialogTitle>
          </DialogHeader>

          {targetPicker?.kind === 'food' && (
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={() => setTargetPicker(p => ({ ...p, mode: 'copy' }))}
                className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                  targetPicker.mode === 'copy' ? 'bg-primary text-white' : 'bg-muted hover:bg-muted/80'
                }`}
              >
                <Copy className="h-3 w-3" /> {t('dietSuggestion.copy', 'Copy')}
              </button>
              <button
                type="button"
                onClick={() => setTargetPicker(p => ({ ...p, mode: 'move' }))}
                className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                  targetPicker.mode === 'move' ? 'bg-primary text-white' : 'bg-muted hover:bg-muted/80'
                }`}
              >
                <ArrowRightLeft className="h-3 w-3" /> {t('dietSuggestion.move', 'Move')}
              </button>
            </div>
          )}

          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {(() => {
              if (!targetPicker) return null;
              // Group meals by type for target listing
              const seen = new Set();
              const uniqueTypes = [];
              suggestionForm.meals.forEach(m => {
                if (!seen.has(m.meal_type)) { seen.add(m.meal_type); uniqueTypes.push(m.meal_type); }
              });

              return uniqueTypes.map(mt => {
                const siblings = suggestionForm.meals
                  .map((m, i) => ({ m, i }))
                  .filter(({ m }) => m.meal_type === mt);
                const mealTypeKey = mt.toLowerCase();
                const label = ['breakfast', 'lunch', 'dinner', 'snack'].includes(mealTypeKey)
                  ? t(`diet.${mealTypeKey === 'snack' ? 'snacks' : mealTypeKey}`)
                  : mt.charAt(0).toUpperCase() + mt.slice(1);
                return (
                  <div key={mt} className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {siblings.map(({ m, i }) => {
                        const isSource =
                          targetPicker.kind === 'food'
                            ? i === targetPicker.sourceMealIdx
                            : targetPicker.kind === 'option' && i === targetPicker.sourceMealIdx;
                        return (
                          <button
                            key={m._tabId ?? i}
                            type="button"
                            disabled={isSource}
                            onClick={() => applyTargetPicker(i)}
                            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors border ${
                              isSource
                                ? 'bg-muted/30 text-muted-foreground/50 border-border/20 cursor-not-allowed'
                                : 'bg-muted hover:bg-primary/20 hover:text-primary border-border/40'
                            }`}
                            data-testid={`target-${mt}-${i}`}
                          >
                            {m.option_name || t('dietSuggestion.single', 'Single')}
                            {m.foods?.length > 0 && <span className="ml-1 opacity-60">({m.foods.length})</span>}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => applyTargetPicker({ newOptionOf: mt })}
                        className="px-2.5 py-1 rounded text-xs font-medium transition-colors border border-dashed border-primary/40 text-primary/80 hover:bg-primary/10"
                        data-testid={`target-${mt}-new-option`}
                      >
                        <Plus className="h-2.5 w-2.5 inline" /> {t('dietSuggestion.newOption', 'New option')}
                      </button>
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTargetPicker(null)}>{t('common.cancel')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved changes confirmation */}
      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogContent className="max-w-[340px] rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.unsavedChanges', 'Unsaved Changes')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('common.discardChangesMessage', 'You have unsaved changes. Are you sure you want to discard them?')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.keepEditing', 'Keep Editing')}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90 text-destructive-foreground" onClick={() => { setShowDiscardConfirm(false); onOpenChange(false); }}>
              {t('common.discard', 'Discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
